import { useQuery, QueryClient } from '@tanstack/react-query';
import {
  viewerQueryOptions,
  type LayoutHotspotSummary,
} from '@ironview/interactive-viewer';
import { getPaginatedHotspots } from '../api/api';
import type { LayoutHotspotListItem } from '../types/types';
import { normalizeIronview360LayoutHotspot } from '../adapters/ironview360HotspotNormalizer';
import {
  createIronview360ViewerContext,
  ironview360ViewerAdapter,
} from '../adapters/ironview360DataAdapter';
import { fromViewerLayoutHotspotSummary } from '../adapters/ironview360Adapter';
import { invalidateIronview360LayoutHotspots } from '../adapters/ironview360ViewerCache';

export interface UseLayoutHotspotsResult {
  hotspots: LayoutHotspotListItem[];
  isLoading: boolean;
  error: Error | null;
  totalHotspots: number;
  isRefetching: boolean;
  metrics: {
    totalOverlays: number;
  };
}

export interface PaginatedHotspotsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: LayoutHotspotListItem[];
}

export interface UsePaginatedHotspotsResult {
  data: PaginatedHotspotsResponse | undefined;
  hotspots: LayoutHotspotListItem[];
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  metrics: {
    totalOverlays: number;
  };
}

/**
 * Hook to fetch all hotspots for a layout (ironview360: layout name, photo index, title, created_by, overlay count).
 */
export const useLayoutHotspots = (
  layoutId: string | undefined,
  projectId?: string,
): UseLayoutHotspotsResult => {
  const context = createIronview360ViewerContext(projectId ?? '');
  const {
    data: hotspots = [],
    isLoading,
    error,
    isRefetching,
  } = useQuery<
    readonly LayoutHotspotSummary[],
    Error,
    LayoutHotspotListItem[]
  >({
    ...viewerQueryOptions.layoutHotspots(
      ironview360ViewerAdapter,
      context,
      layoutId ?? '',
    ),
    enabled: !!layoutId && !!projectId,
    select: (summaries) =>
      summaries.map(fromViewerLayoutHotspotSummary),
    staleTime: 1000 * 60 * 3,
    gcTime: 1000 * 60 * 15,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: (failureCount, err) => {
      if (err && typeof err === 'object' && 'status' in err && (err as { status: number }).status === 404) return false;
      return failureCount < 2;
    },
    refetchOnReconnect: 'always',
  });

  const totalOverlays = hotspots.reduce((sum, h) => sum + (h.overlay_count ?? 0), 0);

  return {
    hotspots,
    isLoading,
    error: error as Error | null,
    totalHotspots: hotspots.length,
    isRefetching,
    metrics: { totalOverlays },
  };
};

export default useLayoutHotspots;

/**
 * Utility to invalidate layout hotspots cache
 */
export const invalidateLayoutHotspots = (queryClient: QueryClient, layoutId?: string) => {
  void invalidateIronview360LayoutHotspots(queryClient, layoutId);
};

/**
 * Hook to fetch paginated hotspots for a project (ironview360).
 * Pass searchTerm to filter server-side; when empty, all hotspots for the page are returned.
 */
export const usePaginatedHotspots = (
  projectId: string | undefined,
  page: number = 1,
  pageSize: number = 20,
  searchTerm?: string
): UsePaginatedHotspotsResult => {
  const search = searchTerm?.trim() ?? '';
  const {
    data: rawData,
    isLoading,
    isFetching,
    error,
  } = useQuery<PaginatedHotspotsResponse>({
    queryKey: ['paginatedHotspots', projectId, page, pageSize, search],
    queryFn: () => getPaginatedHotspots(projectId!, page, pageSize, search || undefined),
    enabled: !!projectId,
    staleTime: 1000 * 60 * 3,
    gcTime: 1000 * 60 * 15,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: (failureCount, err) => {
      if (err && typeof err === 'object' && 'status' in err && (err as { status: number }).status === 404) return false;
      return failureCount < 2;
    },
    refetchOnReconnect: 'always',
    placeholderData: (previousData) => previousData,
  });

  const rawList = Array.isArray(rawData?.results) ? rawData.results : [];
  const hotspots: LayoutHotspotListItem[] = rawList
    .map(normalizeIronview360LayoutHotspot)
    .filter((h): h is LayoutHotspotListItem => h != null);
  const totalOverlays = hotspots.reduce((sum, h) => sum + (h.overlay_count ?? 0), 0);

  return {
    data: rawData,
    hotspots,
    isLoading,
    isFetching,
    error: error as Error | null,
    metrics: { totalOverlays },
  };
};
